import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IFacebookPage } from '@feathr/blackbox';
import useMetaIntegrationStatus from '@feathr/extender/hooks/useMetaIntegrationStatus';
import { useAccount, useFlags, useStore } from '@feathr/extender/state';

import type { TMetaCardState } from './ChannelsCard.utils';
import { getMetaCardState, getMetaLockedTooltip } from './ChannelsCard.utils';

/**
 * Return type for the useChannelsCard hook.
 */
export interface IUseChannelsCardResult {
  /** Whether Display Advertising is selected */
  displaySelected: boolean;
  /** Whether Meta Advertising is selected */
  metaSelected: boolean;
  /** Handler for Display card selection changes */
  handleDisplayChange: (checked: boolean) => void;
  /** Handler for Meta card selection changes */
  handleMetaChange: (checked: boolean) => void;
  /** Current state of the Meta card (normal, locked, or setup) */
  metaCardState: TMetaCardState;
  /** Tooltip text when Meta card is locked, undefined otherwise */
  metaLockedTooltip: string | undefined;
  /** Whether the Channels card should be shown (metaV2 flag enabled) */
  showChannelsCard: boolean;
  /** Available Facebook pages for selection */
  facebookPages: IFacebookPage[];
  /** Whether Facebook pages are currently loading */
  isLoadingFacebookPages: boolean;
  /** Currently selected Facebook page */
  selectedFacebookPage: IFacebookPage | undefined;
  /** Handler for Facebook page selection */
  handleFacebookPageSelect: (page: IFacebookPage) => void;
}

/**
 * Custom hook that manages state and logic for the ChannelsCard component.
 *
 * Handles:
 * - Channel selection state (display/meta)
 * - Meta card state based on account tier and integration status
 * - Feature flag gating
 *
 * @returns Object containing selection state, handlers, and computed values
 */
export function useChannelsCard(): IUseChannelsCardResult {
  const { t } = useTranslation();
  const account = useAccount();
  const { FacebookPages } = useStore();
  const { metaV2 = false } = useFlags();
  const { hasExistingIntegration } = useMetaIntegrationStatus();

  const [displaySelected, setDisplaySelected] = useState(true);
  const [metaSelected, setMetaSelected] = useState(false);
  const [selectedFacebookPage, setSelectedFacebookPage] = useState<IFacebookPage | undefined>();

  const isPlatformLite = account.isPlatformLite;

  const metaCardState = getMetaCardState({
    isPlatformLite,
    hasExistingIntegration,
  });

  const metaLockedTooltip = metaCardState === 'locked' ? getMetaLockedTooltip(t) : undefined;

  const facebookPagesCollection = FacebookPages.list();
  const isLoadingFacebookPages = facebookPagesCollection.isPending;

  const facebookPages = useMemo(() => {
    if (isLoadingFacebookPages) {
      return [];
    }
    return facebookPagesCollection.models
      .map((page) => page.toJS())
      .filter((page) => page.feathr_has_agency);
  }, [isLoadingFacebookPages, facebookPagesCollection.models]);

  /**
   * Handles Display card selection changes.
   */
  function handleDisplayChange(checked: boolean): void {
    setDisplaySelected(checked);
  }

  /**
   * Handles Meta card selection changes.
   * Only allows selection when Meta card is in 'normal' state.
   */
  function handleMetaChange(checked: boolean): void {
    if (metaCardState !== 'normal') {
      return;
    }
    setMetaSelected(checked);
  }

  /**
   * Handles Facebook page selection.
   */
  function handleFacebookPageSelect(page: IFacebookPage): void {
    setSelectedFacebookPage(page);
  }

  return {
    displaySelected,
    metaSelected,
    handleDisplayChange,
    handleMetaChange,
    metaCardState,
    metaLockedTooltip,
    showChannelsCard: metaV2,
    facebookPages,
    isLoadingFacebookPages,
    selectedFacebookPage,
    handleFacebookPageSelect,
  };
}
